import Head from 'next/head'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'

import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'

import { useGetPlaylistById } from 'api/video-repository/useAPIPlaylists'
import { VideoShimmer } from 'app/VideoRepository/components/Preloader'
import DesktopVideoSidebar from 'app/VideoRepository/components/DesktopVideoSidebar'
import Video from 'app/VideoRepository/Video'
import { useVideoInspirasiDesktopView } from 'app/VideoRepository/hooks/useVideoInspirasiDesktopView'
import { PageContainer, DesktopPageHeader } from 'components/ResponsiveLayout'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import { SocialShareDrawer } from 'components/SocialShareDrawer'
import AndroidHandler from 'utils/androidHandler'

import type { PlaylistVideo } from 'types/video-inspirasi'

const HELPDESK_LINK = 'https://pusatinformasi.guru.kemdikbud.go.id'

const VideoPage = () => {
  const { query } = useRouter()
  const { data, isSuccess, isError, isLoading } = useGetPlaylistById(
    query.id as string
  )
  const [currentVideo, setCurrentVideo] = useState<PlaylistVideo | null>(null)
  const [currentVideoId, setCurrentVideoId] = useState<string>(
    query.video as string
  )
  const [showShareDialog, setShowShareDialog] = useState(false)
  const androidHandler = AndroidHandler()
  const router = useRouter()
  const { isDesktop } = useVideoInspirasiDesktopView()

  const shareUrl = `${
    typeof window !== 'undefined'
      ? window.location.origin
      : process.env.NEXT_PUBLIC_BASE_URL
  }/video-inspirasi/playlists/video?id=${query.id}&video=${
    currentVideoId || query.video
  }`

  useEffect(() => {
    if (query.video) {
      setCurrentVideoId(query.video as string)
    }
  }, [query.video])

  useEffect(() => {
    if (isSuccess) {
      const videoId = currentVideoId || (query.video as string)
      const video = data.data?.videos.find((v) => {
        return v.videoId === videoId
      })
      if (!!video) {
        setCurrentVideo(() => video)
      }
    }
  }, [data?.data?.videos, isSuccess, currentVideoId, query?.video])

  const triggerShareDialog = () => {
    if (androidHandler.enabled) {
      androidHandler.shareToolkitUrl(
        `Lihat video: ${currentVideo.title} di ${shareUrl}`
      )
    } else {
      setShowShareDialog(true)
    }
  }

  const handleVideoSelect = (videoId: string) => {
    setCurrentVideoId(videoId)
  }

  if (isDesktop) {
    return (
      <>
        <Head>
          <title>Video Inspirasi | Video {currentVideo?.title ?? ''} </title>
        </Head>

        <div className="min-h-screen bg-surface-subdued">
          <DesktopPageHeader
            navLinks={[
              { label: 'Bantuan', href: HELPDESK_LINK, external: true },
            ]}
            breadcrumbItems={[
              { label: 'Ide Praktik', href: '/video-inspirasi' },
              {
                label: data?.data?.name ?? 'Playlists',
                href: `/video-inspirasi/playlists?id=${query.id}`,
              },
              { label: currentVideo?.title ?? 'Video', href: null },
            ]}
          />
          <main>
            <PageContainer className="py-6">
              {isLoading && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <VideoShimmer />
                </div>
              )}
              {isSuccess && !!currentVideo && (
                <div className="flex gap-6">
                  {/* Main video area */}
                  <div className="flex-1 min-w-0">
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                      <Video
                        playlistId={(query.id as string) ?? ''}
                        title={currentVideo.title}
                        videoId={currentVideo.videoId}
                        description={currentVideo.description}
                        duration={currentVideo.duration}
                        publisherName={currentVideo.publisherName}
                        publishedAt={currentVideo.publishedAt}
                        onShareClick={triggerShareDialog}
                        isDesktop
                      />
                    </div>
                  </div>

                  {/* Sidebar - playlist videos */}
                  <div className="w-[35%] flex-shrink-0">
                    <div className="sticky top-[5rem]">
                      <DesktopVideoSidebar
                        playlistName={data?.data?.name}
                        playlistId={data?.data?.id}
                        videos={data?.data?.videos ?? []}
                        currentVideoId={currentVideo.videoId}
                        onVideoSelect={handleVideoSelect}
                      />
                    </div>
                  </div>
                </div>
              )}
              {isSuccess && !!currentVideo && (
                <SocialShareDrawer
                  isOpen={showShareDialog}
                  title="Bagikan Video"
                  onClose={() => {
                    setShowShareDialog(false)
                  }}
                  shareText={`Lihat video: ${currentVideo.title} `}
                  shareUrl={shareUrl}
                />
              )}
              {isError && (
                <EmptyState
                  title={'Gagal mengambil data'}
                  description={'Data Repository tidak dapat diakses'}
                >
                  <EmptyStateAction onClick={() => window.location.reload()}>
                    Muat Ulang
                  </EmptyStateAction>
                </EmptyState>
              )}
            </PageContainer>
          </main>
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Video Inspirasi | Video {currentVideo?.title ?? ''} </title>
      </Head>

      <HeaderBar fixed className="z-10">
        <HeaderBarLeftAction
          icon={<Icon as="i">arrow_back</Icon>}
          onClick={() => {
            const isNewActivity = window.history.length <= 2
            if (isNewActivity) {
              if (androidHandler.enabled) {
                androidHandler.backToApp()
              } else {
                router.replace(
                  `/video-inspirasi/playlists?id=${router.query.id}`
                )
              }
            } else {
              router.back()
            }
          }}
        />
        <HeaderBarTitle>Video Inspirasi</HeaderBarTitle>
      </HeaderBar>
      <div className="mt-14 pb-16 max-w-xl mx-auto">
        {isLoading && <VideoShimmer />}
        {isSuccess && !!currentVideo && (
          <>
            <Video
              playlistId={(query.id as string) ?? ''}
              title={currentVideo.title}
              videoId={currentVideo.videoId}
              description={currentVideo.description}
              duration={currentVideo.duration}
              publisherName={currentVideo.publisherName}
              publishedAt={currentVideo.publishedAt}
              onShareClick={triggerShareDialog}
            />
            <SocialShareDrawer
              isOpen={showShareDialog}
              title="Bagikan Video"
              onClose={() => {
                setShowShareDialog(false)
              }}
              shareText={`Lihat video: ${currentVideo.title} `}
              shareUrl={shareUrl}
            />
          </>
        )}
        {isError && (
          <EmptyState
            title={'Gagal mengambil data'}
            description={'Data Repository tidak dapat diakses'}
          >
            <EmptyStateAction onClick={() => window.location.reload()}>
              Muat Ulang
            </EmptyStateAction>
          </EmptyState>
        )}
      </div>
    </>
  )
}

VideoPage.Layout = ({ children }) => children

export default VideoPage
